import { useRef } from 'react';
import { Button, ButtonDropdown, Card, CardBody, CardTitle, Col, DropdownItem, DropdownMenu, DropdownToggle, Row } from 'reactstrap';
import useToggle from '../../hook/useToggle';
import { IOrg } from '../../types/org';
import { CancelIcon, PreviewIcon, ReportIcon } from '../common/icons';
import Tabs, { Tab } from '../common/tabs/tabs';
import ContactInfo from './contact-info/contact-info';
import InfoGoConfig from './infogo-config/infogo-config';
import Preview, { PreviewHandler } from './infogo-config/infogo-preview/preview';
import OrgDetails from './org-details/org-details';
import PositionsRoles from './positions-roles/positions-roles';
import { useMutation } from '@apollo/client';
import { GENERATE_REPORT } from '../../graphql/report-gql';
import { ReportType } from '../../types/constants';
import useReport from '../../hook/useReport';
import useAuth from '../../hook/useAuth';
import confirm from '../common/confirm'

type OrgInfoProps = {
    org: IOrg;
    onOrgSelect: (org: IOrg | undefined) => void;
}

const OrgInfo = (props: OrgInfoProps) => {
    const { org, onOrgSelect } = props

    const { generateReport, inProgress } = useReport()

    const tabs: Tab[] = [
        { title: 'Positions & Roles', content: <PositionsRoles org={org} /> },
        { title: 'Organization Details', content: <OrgDetails org={org} onOrgSelect={onOrgSelect} /> },
        { title: 'Contact Information', content: <ContactInfo org={org} /> },
        { title: 'INFO-GO Configuration', content: <InfoGoConfig org={org} /> },
    ];

    const {isAdmin, isCoordinator } = useAuth();

    const { isOpen: previewDdlOpen, toggle: togglePreviewDdl } = useToggle()
    const { isOpen: reportDdlOpen, toggle: toggleReportDdl } = useToggle()

    const previewRef = useRef<PreviewHandler>(null)

    const openPreview = (inter: boolean) => {
        previewRef.current && previewRef.current.open(inter)
    }

    const onGeneateReport = (reportType: ReportType) => {
        generateReport({
            reportType: reportType,
            orgId: String(org.id)
        })

        confirm({
            title: 'Your report is downloading.',
            content: `Your report is being generated. When it's ready, your browser download manager will open so that you can access the report. If you can't find the file, check your download folder. <br /> <br /> Depending on the report size, it may take several minutes to generate and download.`,
            okText: <><CancelIcon/> Close</>,
            okBtnColor: 'secondary',
            titleCenter: true,
            bodyCenter: false,
            showCancel: false,
        })
    }

    return (
        <>
            <Card>
                <CardBody>
                    <CardTitle className="mb-0">
                        <h2 className="d-flex h2-buttons">Organization: {org?.orgName}
                            <Col className="d-flex justify-content-end">

                                { (isAdmin || isCoordinator) &&
                                <ButtonDropdown isOpen={reportDdlOpen} toggle={toggleReportDdl} style={{ marginRight: '0.5rem' }}>
                                    <DropdownToggle caret color="primary">
                                        <ReportIcon /> Report
                                    </DropdownToggle>
                                    <DropdownMenu style={{ overflowY: 'auto' }}>
                                        <DropdownItem onClick={() => onGeneateReport(ReportType.RPT_MINISTRY_DATA)}>Ministry Data Report</DropdownItem>
                                        <DropdownItem onClick={() => onGeneateReport(ReportType.RPT_HIERARCHY)}>Hierarchy Report</DropdownItem>
                                    </DropdownMenu>
                                </ButtonDropdown>
                                }

                                <ButtonDropdown isOpen={previewDdlOpen} toggle={togglePreviewDdl}>
                                    <DropdownToggle caret color="primary">
                                        <PreviewIcon /> Preview
                                    </DropdownToggle>
                                    <DropdownMenu style={{ overflowY: 'auto' }}>
                                        <DropdownItem onClick={() => openPreview(true)}>Internet (External)</DropdownItem>
                                        <DropdownItem onClick={() => openPreview(false)}>Intranet (Internal)</DropdownItem>
                                    </DropdownMenu>
                                </ButtonDropdown>
                            </Col>
                        </h2>
                    </CardTitle>

                    <Tabs tabs={tabs} />
                </CardBody>
            </Card>

            <Preview ref={previewRef} org={org}></Preview>
        </>
    )
}

export default OrgInfo
